import { useCallback, useContext, useState } from 'react';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { currentUser } from 'js/utils/user';
import { FeedContext } from 'js/social/contexts/feed';
import { SnackbarContext } from 'js/social/contexts/snackbar';
import {
  asEnum,
  Comment,
  DismissalReason,
  FollowableUser,
  isPaper,
  News,
  NewsType,
  OpenalexWork,
  Paper,
  PaperType,
  PaperUpdateReason,
  Post,
  PostReason,
  ReactionType,
  ResourcePayload,
  ResourceType,
  Tag,
} from 'js/social/shared/types';
import { createComment as createCommentApi } from 'js/social/api/comment_api';
import {
  createContentFeedback,
  deleteContentFeedback,
} from 'js/social/api/content_feedback_api';
import { editPaperUpdate } from 'js/social/api/paper_update_api';
import { createPost, deletePost, editPost } from 'js/social/api/post_api';
import { createReaction, deleteReaction } from 'js/social/api/reaction_api';
import { reportResource, deleteResourceReport } from 'js/social/api/report_api';
import { deleteTag } from 'js/social/api/tag_api';
import {
  createUserFollowing,
  deleteUserFollowing,
  createUserBlock,
} from 'js/social/api/user_following_api';
import {
  bookmarkWork as bookmarkWorkApi,
  unbookmarkWork as unbookmarkWorkApi,
} from 'js/social/api/work_api';
import { extractNumericIdFromOpenalexId } from 'js/social/shared/utils';

export interface FeedPostsActions {
  /**
   * Update the global users' state to reflect the new value of user
   * following.
   *
   * @param id ID of a target user
   * @param following Whether to follow the target user
   * @return void
   */
  setUserFollowing: (id: number, following: boolean) => void;
  /**
   * Remove all content from a user from being displayed in the feed.
   *
   * @param id ID of a user whose content is to be removed
   * @return void
   */
  removeUserContent: (id: number) => void;
  /**
   * Add a social news to be displayed in the feed.
   *
   * @param post The news to be added
   * @param users The users associated with the comment
   * @return void
   */
  addFeedNews: (post: Post, users: FollowableUser[]) => void;
  /**
   * Add a social comment to be displayed in the feed.
   *
   * @param type The type of commentable
   * @param commentableId The commentable object that the comment belongs to
   * @param parentCommentId The parent comment id of the comment, if any
   * @param comment The comment to be added
   * @param users The users associated with the comment
   * @return void
   */
  addFeedComment: (
    type: NewsType,
    commentableId: number | string,
    parentCommentId: number | string | null,
    comment: Comment,
    users: FollowableUser[],
  ) => void;
  /**
   * Remove a social news from being displayed in the feed.
   *
   * @param id ID of a target social news
   * @param type The type of news
   * @return void
   */
  removeFeedNews: (id: number | string, type: NewsType) => void;
  /**
   * Update data for a given social news.
   *
   * @param id The ID of the social news.
   * @param type The type of news.
   * @param data The updated news object.
   */
  updateFeedNews: (id: number | string, type: NewsType, update: News) => void;
  /**
   * Update feed's state to reflect user has liked news
   *
   * @param id ID of a target social news
   * @param type The type of news
   * @return void
   */
  likeFeedNews: (id: number | string, type: NewsType) => void;
  /**
   * Update feed's state to reflect user has unliked news
   *
   * @param id ID of a target social news
   * @param type The type of news
   * @return void
   */
  unlikeFeedNews: (id: number | string, type: NewsType) => void;
  /**
   * Update feed's state to reflect user has reposted news
   *
   * @param id ID of a target social news
   * @param type The type of news
   * @return void
   */
  repostFeedNews: (id: number | string, type: NewsType) => void;
  /**
   * Update feed's state to reflect user has reposted news
   *
   * @param id ID of a target social news
   * @param type The type of news
   * @return void
   */
  unrepostFeedNews: (id: number | string, type: NewsType) => void;
  /**
   * Update feed's state to reflect user has bookmarked work associated with news
   *
   * @param workId ID of work
   * @param bookmarked whether the work is bookmarked by the user
   * @return void
   */
  updateWorkBookmarkStatus: (workId: number, bookmarked: boolean) => void;
}

type MessageText = {
  bookmarkSuccess?: string;
  bookmarkFailure?: string;
  unbookmarkSuccess?: string;
  unbookmarkFailure?: string;
};

/**
 * This handles the various interactions a user can perform on social news
 */
const useNewsInteractions = ({
  newsId,
  newsType,
  paperType,
  userId,
  tags,
  reason,
  createdAt,
  work,
  followSource,
  setUserFollowing,
  removeUserContent,
  addFeedNews,
  addFeedComment,
  removeFeedNews,
  updateFeedNews,
  likeFeedNews,
  unlikeFeedNews,
  repostFeedNews,
  unrepostFeedNews,
  updateWorkBookmarkStatus,
  messageText,
}: {
  newsId: number | string;
  newsType: NewsType;
  paperType: PaperType | null;
  userId?: number;
  tags: Tag[];
  reason?: PostReason | PaperUpdateReason;
  createdAt?: string;
  work?: Paper | OpenalexWork;
  followSource?: string;
  messageText?: MessageText;
} & FeedPostsActions) => {
  const {
    feedConfig: { page },
  } = useContext(FeedContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const [dismissalId, setDismissalId] = useState<number | string | undefined>();
  const [reasonForDismissal, setReasonForDismissal] = useState<
    DismissalReason | undefined
  >();

  const sendableId =
    newsType === 'openalex_work'
      ? extractNumericIdFromOpenalexId(work?.id)
      : newsId;

  const followUser = useCallback(async () => {
    if (!userId) return;
    await createUserFollowing(userId, followSource || 'social_news_feed', {
      item_id: newsId,
      item_type: newsType,
      item_reason: reason?.type,
    });
    setUserFollowing(userId, true);
  }, [userId, newsId, newsType, reason, setUserFollowing, followSource]);

  const unfollowUser = useCallback(async () => {
    if (!userId) return;
    await deleteUserFollowing(userId);
    setUserFollowing(userId, false);
  }, [userId, setUserFollowing]);

  const blockUser = useCallback(async () => {
    if (!userId) return;
    await createUserBlock(userId);
    removeUserContent(userId);
  }, [userId, removeUserContent]);

  const reportNews = useCallback(
    async (reason?: string) => {
      const response = await reportResource(
        newsId,
        asEnum(ResourceType, newsType),
        reason,
      );
      setDismissalId(response.id);
      setReasonForDismissal(DismissalReason.Reported);
    },
    [newsId, newsType, setReasonForDismissal],
  );

  const unReportNews = useCallback(async () => {
    if (reasonForDismissal !== DismissalReason.Reported) return;
    if (!dismissalId) return;
    await deleteResourceReport(dismissalId);
    setReasonForDismissal(undefined);
  }, [dismissalId, reasonForDismissal, setReasonForDismissal]);

  const ignoreNews = useCallback(
    async (feedbackType: number, reason: number) => {
      const response = await createContentFeedback(
        newsId,
        asEnum(ResourceType, newsType),
        feedbackType,
        reason,
      );
      setDismissalId(response.id);
      setReasonForDismissal(DismissalReason.Ignored);
    },
    [newsId, newsType, setReasonForDismissal, setDismissalId],
  );

  const unIgnoreNews = useCallback(async () => {
    if (reasonForDismissal !== DismissalReason.Ignored) return;
    if (!dismissalId) return;
    await deleteContentFeedback(
      newsId,
      dismissalId,
      asEnum(ResourceType, newsType),
    );
    setReasonForDismissal(undefined);
  }, [
    newsId,
    dismissalId,
    newsType,
    reasonForDismissal,
    setReasonForDismissal,
  ]);

  const deleteNews = useCallback(async () => {
    if (newsType !== 'post') {
      return;
    }
    await deletePost(newsId);
    removeFeedNews(newsId, newsType);
  }, [newsId, newsType, removeFeedNews]);

  const likeNews = useCallback(async () => {
    await createReaction(
      sendableId,
      asEnum(ResourceType, newsType),
      ReactionType.Like,
    );
    likeFeedNews(newsId, newsType);
  }, [sendableId, newsId, newsType, likeFeedNews]);

  const unlikeNews = useCallback(async () => {
    await deleteReaction(
      sendableId,
      asEnum(ResourceType, newsType),
      ReactionType.Like,
    );
    unlikeFeedNews(newsId, newsType);
  }, [sendableId, newsId, newsType, unlikeFeedNews]);

  const createComment = useCallback(
    async ({ content, tags }: ResourcePayload) => {
      const { comment, user } = await createCommentApi(
        content,
        tags,
        newsId,
        newsType,
      );
      addFeedComment(newsType, newsId, null, comment, [user]);
    },
    [newsId, newsType, addFeedComment],
  );

  const repostNews = useCallback(async () => {
    await createReaction(
      newsId,
      asEnum(ResourceType, newsType),
      ReactionType.Repost,
    );
    repostFeedNews(newsId, newsType);
  }, [newsId, newsType, repostFeedNews]);

  const unrepostNews = useCallback(async () => {
    await deleteReaction(
      newsId,
      asEnum(ResourceType, newsType),
      ReactionType.Repost,
    );
    unrepostFeedNews(newsId, newsType);
  }, [newsId, newsType, unrepostFeedNews]);

  const createQuotedNews = useCallback(
    async ({ content, tags, image, paper }: ResourcePayload) => {
      if (userId !== currentUser?.id && content.trim() === '') {
        await repostNews();
      } else {
        const { post, user } = await createPost({
          content,
          tags,
          quotableType: newsType,
          quotableId: newsId,
          image,
          paper,
        });
        if (page === 'single_post') {
          window.location.href = routes.community_show_post_url({
            post_id: post.newsId,
          });
        } else {
          addFeedNews(post, [user]);
        }
      }
    },
    [newsType, userId, repostNews, newsId, page, addFeedNews],
  );

  const removeTag = useCallback(async () => {
    const news = await deleteTag(newsId, asEnum(ResourceType, newsType));
    updateFeedNews(newsId, newsType, news);
  }, [newsId, newsType, updateFeedNews]);

  const editNews = useCallback(
    async ({ content, tags }: ResourcePayload) => {
      if (newsType === 'post') {
        const news = await editPost(newsId, content, tags);
        updateFeedNews(newsId, newsType, news.post);
      } else if (newsType === 'paper_update') {
        const news = await editPaperUpdate(newsId, content, tags);
        updateFeedNews(newsId, newsType, news.paper_update);
      }
    },
    [newsId, newsType, updateFeedNews],
  );

  const bookmarkWork = useCallback(async () => {
    if (work === undefined) return;
    let contentType = 'OpenalexWork';
    let workId: number;
    if (isPaper(work)) {
      contentType = 'Work';
      workId = work.id;
    } else {
      workId = parseInt(extractNumericIdFromOpenalexId(work.id));
    }

    await bookmarkWorkApi(workId, contentType).then((res) => {
      if (res.status === 200) {
        updateWorkBookmarkStatus(work.id as number, true);
        showSnackbar(messageText?.bookmarkSuccess ?? 'Bookmarked', 'success');
      } else {
        showSnackbar(
          messageText?.bookmarkFailure ?? 'Failed to bookmark work',
          'failure',
        );
      }
    });
  }, [work, updateWorkBookmarkStatus, showSnackbar, messageText]);

  const unbookmarkWork = useCallback(async () => {
    if (work === undefined) return;
    let contentType = 'OpenalexWork';
    let workId: number;
    if (isPaper(work)) {
      contentType = 'Work';
      workId = work.id;
    } else {
      workId = parseInt(extractNumericIdFromOpenalexId(work.id));
    }
    await unbookmarkWorkApi(workId, contentType).then((res) => {
      if (res.status === 200) {
        updateWorkBookmarkStatus(work.id as number, false);
        showSnackbar(
          messageText?.unbookmarkSuccess ?? 'Bookmark removed',
          'success',
        );
      } else {
        showSnackbar(
          messageText?.unbookmarkFailure ?? 'Failed to remove bookmark',
          'failure',
        );
      }
    });
  }, [work, updateWorkBookmarkStatus, showSnackbar, messageText]);

  const isEligiblePageForFollowing = [
    'home',
    'single_post',
    'research_interest',
  ].includes(page);

  const isItemOwner = userId === currentUser?.id;
  const isLoggedIn = currentUser != null;
  const isTagged = tags.some(
    (tag) => tag.tagType === 'user' && tag.tagId === currentUser?.id,
  );
  const lessThanFourteenDaysOld = createdAt
    ? Date.parse(createdAt) > Date.now() - 1209600000 // 14 days in ms
    : true;
  const isUpload = newsType === 'paper_update' && paperType === 'paper_upload';

  return {
    actions: {
      followUser,
      unfollowUser,
      blockUser,
      reportNews,
      unReportNews,
      ignoreNews,
      unIgnoreNews,
      deleteNews,
      likeNews,
      unlikeNews,
      createQuotedNews,
      createComment,
      repostNews,
      unrepostNews,
      removeTag,
      editNews,
      bookmarkWork,
      unbookmarkWork,
      setReasonForDismissal,
    },
    permissions: {
      isBookmarked: work?.bookmarkedByCurrentUser,
      canBulkDownload:
        newsType === 'paper_update' && (currentUser?.premium || false),
      canFollow: isLoggedIn && !isItemOwner && isEligiblePageForFollowing,
      canBlock: isLoggedIn && !isItemOwner,
      canReport: isLoggedIn && !isItemOwner,
      canDelete: newsType === 'post' && isItemOwner,
      canIgnore: page === 'home' && !isItemOwner,
      canRepost: !isItemOwner,
      canUntag: isTagged,
      canEdit:
        isItemOwner &&
        lessThanFourteenDaysOld &&
        (newsType === 'post' || isUpload),
    },
    reasonForDismissal,
  };
};

export default useNewsInteractions;
